// API Service for Cloudflare Worker backend
// Handles communication between frontend and Worker API

// Use Worker URL directly (production)
const WORKER_URL = 'https://clystcore-api.js-55b.workers.dev'
const API_BASE = `${WORKER_URL}/api`

// Helper to make API requests
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  }

  const response = await fetch(url, config)

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || `API error: ${response.status}`)
  }

  return response.json()
}

// Survey API
export const surveyApi = {
  // List all surveys
  async list() {
    const { surveys } = await apiRequest('/surveys')
    return surveys
  },

  // Get single survey with photos
  async get(id) {
    const data = await apiRequest(`/surveys/${id}`)
    return data
  },

  // Create new survey
  async create(surveyData) {
    const { survey } = await apiRequest('/surveys', {
      method: 'POST',
      body: JSON.stringify(surveyData)
    })
    return survey
  },

  // Update survey
  async update(id, updates) {
    const { survey } = await apiRequest(`/surveys/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    })
    return survey
  },

  // Delete survey
  async delete(id) {
    await apiRequest(`/surveys/${id}`, { method: 'DELETE' })
  },

  // Run AI analysis (all categories)
  async analyse(id) {
    const { results } = await apiRequest(`/surveys/${id}/analyse`, {
      method: 'POST'
    })
    return results
  },

  // Run AI analysis for single category
  async analyseCategory(id, categoryCode) {
    const result = await apiRequest(`/surveys/${id}/analyse/${categoryCode}`, {
      method: 'POST'
    })
    return result
  },

  // Generate document
  async generateDocument(id) {
    const data = await apiRequest(`/surveys/${id}/generate`, {
      method: 'POST'
    })
    return data
  }
}

// Photo API
export const photoApi = {
  // Upload photo to R2
  async upload(surveyId, categoryCode, imageBlob, thumbnailBlob = null) {
    // Step 1: Get upload URLs from Worker
    const { photoId, r2Key, thumbnailR2Key } = await apiRequest('/photos/upload', {
      method: 'POST',
      body: JSON.stringify({ surveyId, categoryCode })
    })

    // Step 2: Upload image directly to R2 via Worker
    // For now, we'll send base64 to the Worker which will store it
    const imageBase64 = await blobToBase64(imageBlob)
    const thumbnailBase64 = thumbnailBlob ? await blobToBase64(thumbnailBlob) : null

    // Step 3: Confirm upload with image data
    const { photo } = await apiRequest('/photos/confirm', {
      method: 'POST',
      body: JSON.stringify({
        photoId,
        surveyId,
        categoryCode,
        r2Key,
        thumbnailR2Key,
        imageData: imageBase64,
        thumbnailData: thumbnailBase64,
        analyseWithAI: true
      })
    })

    return photo
  },

  // Update photo (notes, analyseWithAI)
  async update(photoId, updates) {
    const { photo } = await apiRequest(`/photos/${photoId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    })
    return photo
  },

  // Delete photo
  async delete(photoId) {
    await apiRequest(`/photos/${photoId}`, { method: 'DELETE' })
  }
}

// Helper: Convert blob to base64
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// Helper: Convert image to WebP format
export async function convertToWebP(blob, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height

      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)

      canvas.toBlob(
        (webpBlob) => {
          if (webpBlob) {
            resolve(webpBlob)
          } else {
            reject(new Error('Failed to convert to WebP'))
          }
        },
        'image/webp',
        quality
      )
    }
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(blob)
  })
}

// Helper: Convert image to JPEG format (for PDF compatibility)
export async function convertToJPEG(blob, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height

      const ctx = canvas.getContext('2d')
      // Fill with white background (JPEG has no transparency)
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0)

      canvas.toBlob(
        (jpegBlob) => {
          if (jpegBlob) {
            resolve(jpegBlob)
          } else {
            reject(new Error('Failed to convert to JPEG'))
          }
        },
        'image/jpeg',
        quality
      )
    }
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(blob)
  })
}

// Prompts API (for custom AI prompts that sync across devices)
export const promptsApi = {
  // Get all prompts (merged defaults + custom)
  async getAll() {
    const { prompts } = await apiRequest('/prompts')
    return prompts
  },

  // Get single prompt
  async get(categoryCode) {
    const prompt = await apiRequest(`/prompts/${categoryCode}`)
    return prompt
  },

  // Save/update custom prompt
  async save(categoryCode, prompt) {
    const result = await apiRequest(`/prompts/${categoryCode}`, {
      method: 'PUT',
      body: JSON.stringify({ prompt })
    })
    return result
  },

  // Reset prompt to default
  async reset(categoryCode) {
    const result = await apiRequest(`/prompts/${categoryCode}`, {
      method: 'DELETE'
    })
    return result
  }
}

// Custom Categories API (for user-defined categories)
export const customCategoriesApi = {
  // Get all custom categories
  async getAll() {
    const { categories } = await apiRequest('/custom-categories')
    return categories
  },

  // Create new custom category
  async create(categoryData) {
    const { category } = await apiRequest('/custom-categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    })
    return category
  },

  // Update custom category
  async update(id, updates) {
    const { category } = await apiRequest(`/custom-categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    })
    return category
  },

  // Delete custom category
  async delete(id) {
    await apiRequest(`/custom-categories/${id}`, { method: 'DELETE' })
  }
}

// Health check
export async function checkApiHealth() {
  try {
    const data = await apiRequest('/health')
    return { ok: true, ...data }
  } catch (error) {
    return { ok: false, error: error.message }
  }
}

export default {
  survey: surveyApi,
  photo: photoApi,
  prompts: promptsApi,
  customCategories: customCategoriesApi,
  checkHealth: checkApiHealth,
  convertToWebP
}
